import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import api from "../lib/api";

export default function Journey() {
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [j, setJ] = useState<any>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api.get("/learn/journey").then((r) => setJ(r.data)).catch(() => setLoadError(true));
  }, []);

  if (loadError) return <p className="text-[#FF3B30]" data-testid="journey-error">{t("common.error")}</p>;
  if (!j) return <p className="text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div className="space-y-8" data-testid="owner-journey">
      <div className="fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("journey.title")}</h1>
        <p className="text-sm text-muted-foreground mt-2" data-testid="human-decision-note">{t("journey.humanDecision")}</p>
      </div>

      <div className="swiss-card p-6 sm:p-8 overflow-x-auto fade-up-1" data-testid="journey-timeline">
        <div className="flex items-center gap-0 min-w-[720px]">
          {j.stages.map((s: string, i: number) => {
            const done = j.completed_stages.includes(s);
            const current = s === j.current_stage;
            const next = s === j.next_stage;
            return (
              <React.Fragment key={s}>
                {i > 0 && <div className={`h-0.5 flex-1 min-w-6 ${done || current ? "bg-[#002BF6]" : "bg-border"}`} />}
                <div className="flex flex-col items-center gap-2 px-1" data-testid={`journey-stage-${s}`}>
                  <div className={`w-9 h-9 rounded-md flex items-center justify-center text-xs font-mono-v font-bold border-2 transition-colors duration-200
                    ${current ? "bg-[#002BF6] border-[#002BF6] text-white" : done ? "bg-emerald-600 border-emerald-600 text-white" : next ? "border-[#FF3B30] text-[#FF3B30] bg-white" : "border-border text-muted-foreground bg-white"}`}>
                    {done ? <CheckCircle2 size={16} /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-mono-v text-center leading-tight max-w-20 ${current ? "font-bold text-[#002BF6]" : next ? "text-[#FF3B30] font-semibold" : "text-muted-foreground"}`}>
                    {s.replace(/_/g, " ")}
                  </span>
                  {current && <span className="text-[9px] uppercase tracking-wider font-bold text-[#002BF6]">{t("journey.current")}</span>}
                  {next && <span className="text-[9px] uppercase tracking-wider font-bold text-[#FF3B30]">{t("journey.next")}</span>}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-up-2">
        <div className="swiss-card p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("journey.recommendedTraining")}</p>
          <div className="mt-4 space-y-2">
            {j.recommended_courses.length === 0 && <p className="text-sm text-muted-foreground">{t("journey.noRecommended")}</p>}
            {j.recommended_courses.map((c: any) => (
              <Link to={`/courses/${c.id}`} key={c.id} data-testid={`journey-recommended-${c.slug}`}
                className="flex items-center justify-between gap-2 px-3 py-2.5 border border-border rounded-md hover:border-[#002BF6] transition-colors duration-200 group">
                <div>
                  <span className="text-sm font-medium">{fr ? c.title_fr : c.title_en}</span>
                  {c.status !== "PUBLISHED" && (
                    <span className="ml-2 text-[10px] font-mono-v px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">{t(`courses.status.${c.status}`)}</span>
                  )}
                </div>
                <ArrowRight size={14} className="text-muted-foreground group-hover:text-[#002BF6]" />
              </Link>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="swiss-card p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("journey.inProgress")}</p>
            <div className="mt-4 space-y-2">
              {j.in_progress_courses.map((c: any) => (
                <Link to={`/courses/${c.id}`} key={c.id} className="flex items-center justify-between px-3 py-2.5 border border-border rounded-md hover:border-[#002BF6] transition-colors duration-200">
                  <span className="text-sm font-medium">{fr ? c.title_fr : c.title_en}</span>
                  <span className="text-xs font-mono-v font-semibold">{c.progress_pct}%</span>
                </Link>
              ))}
              {j.in_progress_courses.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
            </div>
          </div>
          <div className="swiss-card p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("journey.completedTraining")}</p>
            <div className="mt-4 space-y-2">
              {j.completed_courses.map((c: any) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2.5 border border-border rounded-md" data-testid={`journey-completed-${c.slug}`}>
                  <CheckCircle2 size={15} className="text-emerald-600" />
                  <span className="text-sm">{fr ? c.title_fr : c.title_en}</span>
                </div>
              ))}
              {j.completed_courses.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
